import React, { useState, useEffect, useRef } from 'react';
import { Search, Filter, Plus, Eye, Package, AlertTriangle, Clock, XCircle, Box, MoreHorizontal, FileText, Edit, Trash2, ArrowLeft, Upload, Share2, Printer, Star, X, Tag, List, Layers, ShieldCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ExpandableSearchBar from '../components/ExpandableSearchBar';
import BulkProductUploadModal from '../components/BulkProductUploadModal';
import imgProductDetails from '../assets/imgProductDetails.svg';
import totalProductsIcon from '../assets/Total_Products.svg';
import outOfStockIcon from '../assets/Out_of_stock.svg';
import { useTheme } from '../context/ThemeContext';
import { message, Modal } from 'antd';
import ThemeSelect from '../components/ThemeSelect';
import { productsApi } from '../services/api';
import Pagination from '../components/Pagination';
import { ASSET_BASE_URL as ASSET_URL } from '../services/api';

const ProductList = () => {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.textColor === '#FFFFFF';
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeActionId, setActiveActionId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'details'
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [productNameToDelete, setProductNameToDelete] = useState('');
  const [productToDeleteType, setProductToDeleteType] = useState('product');
  const [isDeleting, setIsDeleting] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('products'); // 'products' or 'kits'
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]); // Initialize as empty, will be fetched
  const [activeFilters, setActiveFilters] = useState({
    category: 'All Categories',
    subCategory: 'All SubCategories',
    stockStatus: 'All Status',
    type: 'All Types'
  });
  const [tempFilters, setTempFilters] = useState(activeFilters);

  // Mock Data
  const DEFAULT_IMAGE = imgProductDetails;

  // Static products data - Only 5 products
  const [products, setProducts] = useState([]);
  const [apiStatus, setApiStatus] = useState('checking'); // 'checking', 'online', 'offline'

  const skipFirstProductFetch = useRef(true);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    checkHealth();
  }, []);

  const checkHealth = async () => {
    try {
      const response = await productsApi.getHealth();
      if (response && (response.status === 'success' || response.ok)) {
        setApiStatus('online');
      } else {
        setApiStatus('online'); // Assuming if we get any valid response it's online
      }
    } catch (err) {
      console.error('API Health Check Failed:', err);
      setApiStatus('offline');
      message.warning('API Server is currently offline or unreachable');
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await productsApi.getActiveProductCategories();
      const list = Array.isArray(response) ? response : (response.data || []);
      const categoryNames = list.map(c => c.categoryName || c.name || c);
      setCategories(categoryNames);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const resolveImageUrl = (url) => {
    if (!url || url === '' || url.startsWith('/static')) return null;
    if (url.startsWith('http') || url.startsWith('blob:')) return url;
    return `${ASSET_URL}/${url.replace(/^\/\//, '')}`;
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const [prodRes, kitRes] = await Promise.all([
        productsApi.getAllProducts(),
        productsApi.getAllKits()
      ]);

      const productList = Array.isArray(prodRes) ? prodRes : (prodRes.data || []);
      const kitList = Array.isArray(kitRes) ? kitRes : (kitRes.data || []);

      // Mark kits specifically if not already marked
      const kits = kitList.map(k => ({ ...k, productType: 'kit' }));
      const combined = [...productList, ...kits];

      const mappedData = combined.map(p => ({
        ...p,
        id: p.id || p._id,
        name: p.productName || p.name || p.kitName || 'Untitled',
        price: p.mrp || p.price || p.kitPrice || '0',
        image: resolveImageUrl(p.imageUrl) ||
            resolveImageUrl(p.kitImageUrl) ||
            resolveImageUrl(p.image) ||
            null,
        category: p.category || 'Uncategorized',
        stock: p.stockQuantity ?? p.stock ?? 10,
        status: p.isActive ?? p.status ?? true,
        productType: p.kitItems ? 'kit' : (p.productType || 'product'),
        stockStatus: 'In Stock'
      }));

      // Calculate stock status correctly after mapping
      const finalMappedData = mappedData.map(p => ({
        ...p,
        stockStatus: getStockStatus(p)
      }));

      console.log("data",finalMappedData)
      setProducts(finalMappedData);
      setError(null);
    } catch (err) {
      console.error('Error fetching products/kits (API 25):', err);
      // message.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };
  // Helper function to determine stock status
  const getStockStatus = (product) => {
    const stock = parseInt(product.stock || product.totalUnits || 0);

    if (stock === 0) return 'Out of stock';
    if (stock < 50) return 'Low Stock';

    // Check expiry date
    if (product.expiryDate) {
      const expiryDate = new Date(product.expiryDate);
      const today = new Date();
      const daysUntilExpiry = Math.floor((expiryDate - today) / (1000 * 60 * 60 * 24));

      if (daysUntilExpiry < 90 && daysUntilExpiry > 0) {
        return 'Expiry soon';
      }
    }

    return 'In Stock';
  };

  // Sync tempFilters when modal opens.
  // NOTE: pehle yahan bhi getSubCategories(activeFilters.category) call hoti thi,
  // aur setTempFilters(activeFilters) ke baad neeche wala [tempFilters.category]
  // effect bhi usi category ke liye dobara call kar deta tha — ek hi API 2 baar.
  // Ab sirf tempFilters set karte hain; subcategory fetch neeche wala effect
  // hi karega.
  useEffect(() => {
    if (isFilterModalOpen) {
      setTempFilters(activeFilters);
    }
  }, [isFilterModalOpen, activeFilters]);

  // Fetch subcategories when temp category changes in modal
  useEffect(() => {
    if (tempFilters.category && tempFilters.category !== 'All Categories') {
      const fetchSubs = async () => {
        try {
          const response = await productsApi.getSubCategories(tempFilters.category);
          let subList = [];
          if (Array.isArray(response)) subList = response;
          else if (response && response.data && Array.isArray(response.data)) subList = response.data;
          else if (response && typeof response === 'object') subList = response.subcategories || response.list || [];
          const names = subList.map(s => s.subCategoryName || s.name || s);
          setSubCategories(names);
        } catch (err) {
          console.error('Error fetching subcategories:', err);
        }
      };
      fetchSubs();
    } else {
      setSubCategories([]);
    }
  }, [tempFilters.category]);

  const getStockStatusStyle = (status) => {
    switch (status) {
      case 'In Stock':
        return isDark ? 'text-green-400 bg-green-500/20 border-green-500/30' : 'text-[#4D9C7D] bg-[#E8F5E9] border-[#4D9C7D]';
      case 'Low Stock':
        return isDark ? 'text-amber-400 bg-amber-500/20 border-amber-500/30' : 'text-[#FFC107] bg-[#FFF8E1] border-[#FFC107]';
      case 'Expiry soon':
        return isDark ? 'text-orange-400 bg-orange-500/20 border-orange-500/30' : 'text-[#FD7E14] bg-[#FFF3E0] border-[#FD7E14]';
      case 'Out of stock':
        return isDark ? 'text-red-400 bg-red-500/20 border-red-500/30' : 'text-[#DC3545] bg-[#FFEBEE] border-[#DC3545]';
      default:
        return isDark ? 'text-gray-400 bg-white/10 border-white/20' : 'text-gray-600 bg-gray-100 border-gray-300';
    }
  };

  const toggleActionMenu = (id) => {
    if (activeActionId === id) {
      setActiveActionId(null);
    } else {
      setActiveActionId(id);
    }
  };

  const toggleStatus = async (id) => {
  const original = products.find(p => p.id === id);
  setProducts(prev => prev.map(p => p.id === id ? { ...p, status: !p.status } : p));
  
  try {
    if (original?.productType === 'kit') {
      await productsApi.toggleKitStatus(id);
    } else {
      await productsApi.toggleProductStatus(id); // ✅ Direct productsApi use
    }
    message.success('Status updated successfully');
  } catch (err) {
    setProducts(prev => prev.map(p => p.id === id ? { ...p, status: original?.status } : p));
    console.error('Error toggling status:', err);
    message.error('Failed to update status');
  }
};

  const handleBulkUploadSuccess = (result) => {
    console.log('Bulk upload successful:', result);
    message.success(`Successfully uploaded ${result.successCount} products!`);
    // No refresh needed - using static data
  };

  // Error State for validation
  const [errors, setErrors] = useState({});

  const [editFormData, setEditFormData] = useState({
    title: '', name: '', category: 'Ayurveda', subCategory: 'Juice', url: '',
    packageSize: '', ratings: 4, aboutProduct: '', description: '',
    mrp: '', batchNumber: '', hsnCode: '', tax: '', manufactoryDate: '', expiryDate: '',
    pts: '', ptr: '', ptd: '', b2bDiscount: '', b2cDiscount: '',
    tags: ['Health', 'Instagram', 'salud', 'healthy'],
    attributes: ['Shelf Life: 36 Months', 'Container Type: Plastic Bottle'],
    ingredients: [
      { heading: 'Gudmar', content: 'Blood sugar regulation', img: null },
      { heading: 'Karela', content: 'Insulin sensitivity', img: null }
    ],
    faqs: [
      { question: 'How To Treat Lice', answer: 'Use lotions and shampoos...' }
    ]
  });

  const [tempTag, setTempTag] = useState('');
  const [tempAttribute, setTempAttribute] = useState('');

  const handleViewDetails = (product) => {
    // Navigate to product details page
    navigate(`/products/${product.id}`);
  };

  const handleAddProduct = () => {
    localStorage.removeItem('editingProduct');
    navigate('/products/add');
  };

  const handleEditProduct = (product) => {
    // Store product data in localStorage for editing
    localStorage.setItem('editingProduct', JSON.stringify(product));
    // Navigate to add product page
    navigate('/products/add');
  };

  const handleInputChange = (field, value) => {
    setEditFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: false }));
    }
    if (field === 'category') {
      setEditFormData(prev => ({ ...prev, category: value, subCategory: '' })); // Reset subCategory when category changes
      if (value && value !== 'All Categories') {
        const fetchSubs = async () => {
          try {
            const response = await productsApi.getSubCategories(value);
            let subList = [];
            if (Array.isArray(response)) subList = response;
            else if (response && response.data && Array.isArray(response.data)) subList = response.data;
            else if (response && typeof response === 'object') subList = response.subcategories || response.list || [];
            const names = subList.map(s => s.subCategoryName || s.name || s);
            setSubCategories(names);
          } catch (err) {
            console.error('Error fetching subcategories:', err);
            setSubCategories([]); // Clear subcategories on error
          }
        };
        fetchSubs();
      } else {
        setSubCategories([]); // Clear subcategories if 'All Categories' or no category selected
      }
    }
  };

  const handleAddTag = () => {
    if (tempTag.trim()) {
      setEditFormData(prev => ({ ...prev, tags: [...prev.tags, tempTag] }));
      setTempTag('');
    }
  };

  const handleRemoveTag = (index) => {
    setEditFormData(prev => ({ ...prev, tags: prev.tags.filter((_, i) => i !== index) }));
  };

  const handleAddAttribute = () => {
    if (tempAttribute.trim()) {
      setEditFormData(prev => ({ ...prev, attributes: [...prev.attributes, tempAttribute] }));
      setTempAttribute('');
    }
  };

  const handleRemoveAttribute = (index) => {
    setEditFormData(prev => ({ ...prev, attributes: prev.attributes.filter((_, i) => i !== index) }));
  };

  const handleAddIngredient = () => {
    setEditFormData(prev => ({
      ...prev,
      ingredients: [...prev.ingredients, { heading: '', content: '', img: null }]
    }));
  };

  const handleIngredientChange = (index, field, value) => {
    const newIngredients = [...editFormData.ingredients];
    newIngredients[index][field] = value;
    setEditFormData(prev => ({ ...prev, ingredients: newIngredients }));
  };

  const handleAddFAQ = () => {
    setEditFormData(prev => ({
      ...prev,
      faqs: [...prev.faqs, { question: '', answer: '' }]
    }));
  };

  const handleFAQChange = (index, field, value) => {
    const newFaqs = [...editFormData.faqs];
    newFaqs[index][field] = value;
    setEditFormData(prev => ({ ...prev, faqs: newFaqs }));
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const newImages = files.map(file => URL.createObjectURL(file));
      setSelectedProduct(prev => ({
        ...prev,
        images: [...(prev?.images || []), ...newImages],
        image: newImages[0]
      }));
    }
  };

  const handleUpdate = async () => {
    try {
      const updatedProductData = {
        ...editFormData,
        price: editFormData.mrp.replace('₹ ', '').trim(),
        stock: selectedProduct.totalUnits,
      };

      // Update local state only (no API call)
      setProducts(products.map(p =>
        p.id === selectedProduct.id ? { ...p, ...updatedProductData } : p
      ));

      message.success('Product updated successfully!');

      setViewMode('list');
    } catch (err) {
      console.error('Error updating product:', err);
      message.error('Failed to update product. Please try again.');
    }
  };

  const handleSave = async () => {
    const requiredFields = [
      'name', 'title', 'category', 'packageSize', 'description',
      'mrp', 'batchNumber', 'hsnCode', 'tax', 'manufactoryDate', 'expiryDate',
      'pts', 'ptr', 'ptd', 'b2bDiscount', 'b2cDiscount'
    ];

    const newErrors = {};
    let isValid = true;

    requiredFields.forEach(field => {
      if (!editFormData[field] || editFormData[field].toString().trim() === '') {
        newErrors[field] = true;
        isValid = false;
      }
    });

    if (!selectedProduct?.image && (!selectedProduct?.images || selectedProduct.images.length === 0)) {
      newErrors['image'] = true;
      isValid = false;
    }

    if (editFormData.ingredients.length === 0) {
      newErrors['ingredients'] = true;
      isValid = false;
    }

    setErrors(newErrors);

    if (!isValid) {
      message.warning('Please fill all required fields');
      return;
    }

    try {
      const newProductData = {
        ...editFormData,
        imageUrl: selectedProduct?.image || '',
        images: selectedProduct?.images || [],
        status: true,
        stock: '0',
        price: editFormData.mrp ? editFormData.mrp.replace('₹ ', '').trim() : '0',
      };

      // Add to local state only (no API call)
      const newProduct = {
        ...newProductData,
        id: products.length + 1,
        stockStatus: 'In Stock',
        status: true
      };
      setProducts([...products, newProduct]);

      message.success('Product created successfully!');

      setViewMode('list');
    } catch (err) {
      console.error('Error creating product:', err);
      message.error('Failed to create product. Please try again.');
    }
  };

  const handleDeleteProduct = (product) => {
    setProductToDelete(product.id || product._id);
    setProductNameToDelete(product.name || product.productName || product.kitName);
    setProductToDeleteType(product.productType || 'product');
    setShowDeleteModal(true);
    setActiveActionId(null);
  };

  const confirmDelete = async () => {
    if (productToDelete) {
      setIsDeleting(true);
      try {
        if (productToDeleteType === 'kit') {
          // API 38 – Delete Kit: DELETE /api/product-kits/:id
          await productsApi.deleteKit(productToDelete);
        } else {
          await productsApi.deleteProduct(productToDelete);
        }
        setProducts(products.filter(p => (p.id !== productToDelete && p._id !== productToDelete)));
        message.success(`"${productNameToDelete}" deleted successfully!`);
        setShowDeleteModal(false);
        setProductToDelete(null);
        setProductNameToDelete('');
        setProductToDeleteType('product');
      } catch (err) {
        console.error('Error deleting product/kit (API 38):', err);
        const errorDetail = err.message || (err.data && typeof err.data === 'object' ? err.data.message : err.data) || "Unknown error";
        message.error(`Failed to delete "${productNameToDelete}": ${errorDetail}`);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setProductToDelete(null);
  };

  // Filter products locally for stock and type (since typical category filtering is handled by API 10 now)
  const filteredProducts = products.filter(product => {
    // Stock Status Filter
    const matchesStock =
      activeFilters.stockStatus === 'All Status' ||
      getStockStatus(product) === activeFilters.stockStatus;

    // Type Filter (handled by tabs mainly, but good to have here)
    const matchesType =
      activeFilters.type === 'All Types' ||
      (activeFilters.type === 'Product' && product.productType === 'product') ||
      (activeFilters.type === 'Kit' && product.productType === 'kit');

    return matchesStock && matchesType;
  });

  useEffect(() => {
    const fetchFilteredProducts = async () => {
      // Priority 1: Search query takes precedence if there's text
      if (searchTerm.trim()) {
        try {
          const [prodRes, kitRes] = await Promise.all([
            productsApi.searchProducts(searchTerm),
            productsApi.searchKits(searchTerm)
          ]);
          const prodResults = Array.isArray(prodRes) ? prodRes : (prodRes.data || []);
          const kitResults = Array.isArray(kitRes) ? kitRes : (kitRes.data || []);

          // Explicitly mark kits as kit type for filter consistency
          const mappedKits = kitResults.map(k => ({ ...k, productType: 'kit' }));
          mapAndSetProducts([...prodResults, ...mappedKits]);
        } catch (err) {
          console.error('Search error (API 28/Products):', err);
        }
        return;
      }

      // Priority 2: Category + SubCategory filter (API 11)
      if (activeFilters.category !== 'All Categories' && activeFilters.subCategory !== 'All SubCategories') {
        try {
          const response = await productsApi.getProductsByCategoryAndSubCategory(activeFilters.category, activeFilters.subCategory);
          const results = Array.isArray(response) ? response : (response.data || []);
          mapAndSetProducts(results);
        } catch (err) {
          console.error('Category + SubCategory filter error:', err);
        }
        return;
      }

      // Priority 3: Category filter (API 10/29)
      if (activeFilters.category !== 'All Categories') {
        try {
          const [prodRes, kitRes] = await Promise.all([
            productsApi.getProductsByCategory(activeFilters.category),
            productsApi.getKitsByCategory(activeFilters.category)
          ]);
          const prodResults = Array.isArray(prodRes) ? prodRes : (prodRes.data || []);
          const kitResults = Array.isArray(kitRes) ? kitRes : (kitRes.data || []);
          const mappedKits = kitResults.map(k => ({ ...k, productType: 'kit' }));
          mapAndSetProducts([...prodResults, ...mappedKits]);
        } catch (err) {
          console.error('Category filter error (API 10/29):', err);
        }
        return;
      }

      // Priority 4: Type filter (API 12)
      if (activeFilters.type !== 'All Types') {
        try {
          const response = await productsApi.getProductsByType(activeFilters.type.toUpperCase());
          const results = Array.isArray(response) ? response : (response.data || []);
          mapAndSetProducts(results);
        } catch (err) {
          console.error('Type filter error (API 12):', err);
        }
        return;
      }

      // Default: fetch all if no search and no specific category selected
      fetchProducts();
    };

    // Mount par ye effect bhi chalta tha aur default branch fetchProducts() call
    // karta tha — upar wale mount effect ke saath milakar 2 baar hit hoti thi.
    if (skipFirstProductFetch.current) {
      skipFirstProductFetch.current = false;
      return;
    }

    // Simple debounce
    const debounce = setTimeout(() => {
      fetchFilteredProducts();
    }, 500);

    return () => clearTimeout(debounce);
  }, [searchTerm, activeFilters.category, activeFilters.subCategory]);

  // Reusable mapping function for search and category responses
  const mapAndSetProducts = (rawData) => {
    const mappedData = rawData.map(p => {
      const mappedProduct = {
        ...p,
        id: p.id || p._id,
        name: p.productName || p.name || p.kitName || 'Untitled',
        price: p.mrp || p.price || p.kitPrice || '0',
        // ✅ resolveImageUrl lagao yahan bhi
        image: resolveImageUrl(p.imageUrl) ||
            resolveImageUrl(p.kitImageUrl) ||
            resolveImageUrl(p.image) ||
            null,
        category: p.category || 'Uncategorized',
        stock: p.stockQuantity ?? p.stock ?? 10,
        status: p.isActive ?? p.status ?? true,
        productType: p.kitItems ? 'kit' : (p.productType || 'product')
      };
      return {
        ...mappedProduct,
        stockStatus: getStockStatus(mappedProduct)
      };
    });
    setProducts(mappedData);
  };

  // Separate products and kits
  const regularProducts = filteredProducts.filter(p => p.productType !== 'kit');
  const kitProducts = filteredProducts.filter(p => p.productType === 'kit');

  // Get current list based on active tab
  const productsToDisplay = activeTab === 'products' ? regularProducts : kitProducts;
  
  // Pagination logic
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentProducts = productsToDisplay.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-[#0f172a]' : 'bg-gray-50'}`}>
      {/* Filter Modal */}
      {isFilterModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className={`${isDark ? 'bg-[#1e293b]' : 'bg-white'} rounded-2xl w-full max-w-lg overflow-hidden flex flex-col shadow-2xl animate-scaleIn`}>
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100" style={{ background: currentTheme.color }}>
              <div className="flex items-center gap-2 text-white">
                <Filter className="w-5 h-5" />
                <h2 className="text-xl font-bold">Filter Products</h2>
              </div>
              <button
                onClick={() => setIsFilterModalOpen(false)}
                className="p-1 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-6 h-6 text-white" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Category Filter */}
              <div>
                <label className={`flex items-center gap-2 text-sm font-bold mb-3 tracking-wide ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  <Tag className="w-4 h-4" style={{ color: currentTheme.color }} />
                  PRODUCT CATEGORY
                </label>
                <ThemeSelect
                  value={tempFilters.category}
                  onChange={(val) => {
                    setTempFilters({ ...tempFilters, category: val, subCategory: 'All SubCategories' });
                  }}
                  options={['All Categories', ...categories]}
                  className="w-full"
                  icon={List}
                />
              </div>

              {/* SubCategory Filter */}
              <div>
                <label className={`flex items-center gap-2 text-sm font-bold mb-3 tracking-wide ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  <Layers className="w-4 h-4" style={{ color: currentTheme.color }} />
                  SUB CATEGORY
                </label>
                <ThemeSelect
                  value={tempFilters.subCategory}
                  onChange={(val) => setTempFilters({ ...tempFilters, subCategory: val })}
                  options={['All SubCategories', ...subCategories]}
                  className="w-full"
                  icon={Box}
                />
              </div>

              {/* Stock Status Filter */}
              <div>
                <label className={`flex items-center gap-2 text-sm font-bold mb-3 tracking-wide ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  <Package className="w-4 h-4" style={{ color: currentTheme.color }} />
                  STOCK VALIDATION
                </label>
                <ThemeSelect
                  value={tempFilters.stockStatus}
                  onChange={(val) => setTempFilters({ ...tempFilters, stockStatus: val })}
                  options={['All Status', 'In Stock', 'Low Stock', 'Out of stock', 'Expiry soon']}
                  className="w-full"
                  icon={ShieldCheck}
                />
              </div>

              {/* Product Type Filter */}
              <div>
                <label className={`flex items-center gap-2 text-sm font-bold mb-3 tracking-wide ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  <Layers className="w-4 h-4" style={{ color: currentTheme.color }} />
                  ITEM TYPE
                </label>
                <ThemeSelect
                  value={tempFilters.type}
                  onChange={(val) => setTempFilters({ ...tempFilters, type: val })}
                  options={['All Types', 'Product', 'Kit', 'Ayurvedic', 'Cosmetic', 'Pharma']}
                  className="w-full"
                  icon={Box}
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className={`flex items-center justify-end gap-3 px-6 py-4 border-t rounded-b-2xl ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-100'}`}>
              <button
                onClick={() => {
                  setTempFilters({
                    category: 'All Categories',
                    subCategory: 'All SubCategories',
                    stockStatus: 'All Status',
                    type: 'All Types'
                  });
                }}
                className={`px-5 py-2.5 text-sm font-bold rounded-xl transition-all border ${
                  isDark ? 'text-gray-300 bg-white/5 border-white/10' : 'text-gray-600 bg-white border-gray-200'
                }`}
              >
                Reset All
              </button>
              <button
                onClick={() => {
                  setActiveFilters(tempFilters);
                  setIsFilterModalOpen(false);
                }}
                className="px-8 py-2.5 text-sm font-bold text-white rounded-xl shadow-lg transition-all active:scale-95 hover:brightness-110"
                style={{ backgroundColor: currentTheme.color }}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {viewMode === 'list' && (
        <div className="animate-fadeIn">
          {/* Header Section */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
            <div className="flex flex-col gap-1">
              <h1 className={`text-2xl desktop:text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>Product List</h1>
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${apiStatus === 'online' ? 'bg-green-500' : apiStatus === 'offline' ? 'bg-red-500' : 'bg-gray-400 animate-pulse'}`}></span>
                <span className={`text-xs font-bold uppercase tracking-widest ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  API: {apiStatus}
                </span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
              {/* Search Bar */}
              <ExpandableSearchBar
                searchTerm={searchTerm}
                onSearchChange={(v) => { setSearchTerm(v); setCurrentPage(1); }}
                placeholder="Search by product"
              />

              {/* Filter Button */}
              <button
                onClick={() => setIsFilterModalOpen(true)}
                className={`p-2 desktop:p-3 border rounded-full transition-colors ${isDark ? 'border-white/10 hover:bg-white/5' : 'border-gray-200 hover:bg-gray-50'}`}
              >
                <Filter className={`w-5 h-5 desktop:w-6 desktop:h-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
              </button>

              {/* Bulk Upload Button */}
              <button
                onClick={() => setShowBulkUploadModal(true)}
                className="p-2 desktop:p-3 border-2 rounded-full transition-colors relative group"
                style={{ borderColor: currentTheme.color }}
                title="Bulk Upload Products"
              >
                <Upload className="w-5 h-5 desktop:w-6 desktop:h-6" style={{ color: currentTheme.color }} />
              </button>

              {/* Add Single Product Button */}
              <button
                onClick={handleAddProduct}
                className="p-2 desktop:p-3 text-white rounded-full transition-colors relative group"
                style={{ backgroundColor: currentTheme.color }}
              >
                <Plus className="w-5 h-5 desktop:w-6 desktop:h-6" strokeWidth={2.5} />
              </button>
            </div>
          </div>

          {/* Tabs Section */}
          <div className="mb-6">
            <div className={`flex gap-2 border-b ${isDark ? 'border-white/5' : 'border-gray-200'}`}>
              <button
                onClick={() => setActiveTab('products')}
                className={`px-6 py-3 font-medium text-sm desktop:text-base transition-all relative ${activeTab === 'products'
                  ? ''
                  : 'text-gray-500 hover:text-gray-700'
                  }`}
                style={{ color: activeTab === 'products' ? currentTheme.color : undefined }}
              >
                Product List
                {activeTab === 'products' && (
                  <div
                    className="absolute bottom-0 left-0 right-0 h-0.5"
                    style={{ backgroundColor: currentTheme.color }}
                  ></div>
                )}
                <span className={`ml-2 px-2 py-0.5 text-xs font-bold rounded-full transition-colors ${
                  isDark ? 'bg-white/10 text-white border border-white/10' : 'bg-gray-100 text-gray-600'
                }`}>
                  {regularProducts.length}
                </span>
              </button>

              <button
                onClick={() => setActiveTab('kits')}
                className={`px-6 py-3 font-medium text-sm desktop:text-base transition-all relative ${activeTab === 'kits'
                  ? ''
                  : 'text-gray-500 hover:text-gray-700'
                  }`}
                style={{ color: activeTab === 'kits' ? currentTheme.color : undefined }}
              >
                Kit/Combo List
                {activeTab === 'kits' && (
                  <div
                    className="absolute bottom-0 left-0 right-0 h-0.5"
                    style={{ backgroundColor: currentTheme.color }}
                  ></div>
                )}
                <span className={`ml-2 px-2 py-0.5 text-xs font-bold rounded-full transition-colors ${
                  isDark ? 'bg-white/10 text-white border border-white/10' : 'bg-gray-100 text-gray-600'
                }`}>
                  {kitProducts.length}
                </span>
              </button>
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: currentTheme.color }}></div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
              {error}
            </div>
          )}

          {/* Stats Cards */}
          {!loading && !error && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
                <div className={`${isDark ? 'bg-[#1e293b] border-white/5' : 'bg-white border-gray-100'} p-4 desktop:p-6 rounded-2xl border shadow-sm flex items-center justify-between`}>
                  <div>
                    <p className={`${isDark ? 'text-gray-400' : 'text-gray-500'} text-sm desktop:text-base font-bold mb-1`}>
                      {activeTab === 'products' ? 'Total Products' : 'Total Kits'}
                    </p>
                    <h3 className={`text-2xl desktop:text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                      {activeTab === 'products' ? regularProducts.length : kitProducts.length}
                    </h3>
                  </div>
                  <div className={`w-12 h-12 desktop:w-16 desktop:h-16 ${isDark ? 'bg-blue-500/10' : 'bg-blue-50'} rounded-full flex items-center justify-center`}>
                    <img src={totalProductsIcon} alt="Total Products" className="w-6 h-6 desktop:w-8 desktop:h-8" />
                  </div>
                </div>

                <div className={`${isDark ? 'bg-[#1e293b] border-white/5' : 'bg-white border-gray-100'} p-4 desktop:p-6 rounded-2xl border shadow-sm flex items-center justify-between`}>
                  <div>
                    <p className={`${isDark ? 'text-gray-400' : 'text-gray-500'} text-sm desktop:text-base font-bold mb-1`}>Total Categories</p>
                    <h3 className={`text-2xl desktop:text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                      {[...new Set(products.map(p => p.category))].length}
                    </h3>
                  </div>
                  <div className={`w-12 h-12 desktop:w-16 desktop:h-16 ${isDark ? 'bg-green-500/10' : 'bg-green-50'} rounded-full flex items-center justify-center`}>
                    <Package className="w-6 h-6 desktop:w-8 desktop:h-8 text-green-500" />
                  </div>
                </div>

                <div className={`${isDark ? 'bg-[#1e293b] border-white/5' : 'bg-white border-gray-100'} p-4 desktop:p-6 rounded-2xl border shadow-sm flex items-center justify-between`}>
                  <div>
                    <p className={`${isDark ? 'text-gray-400' : 'text-gray-500'} text-sm desktop:text-base font-bold mb-1`}>Out of Stock</p>
                    <h3 className={`text-2xl desktop:text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                      {currentProducts.filter(p => getStockStatus(p) === 'Out of stock').length}
                    </h3>
                  </div>
                  <div className={`w-12 h-12 desktop:w-16 desktop:h-16 ${isDark ? 'bg-red-500/10' : 'bg-red-50'} rounded-full flex items-center justify-center`}>
                    <img src={outOfStockIcon} alt="Out of Stock" className="w-6 h-6 desktop:w-8 desktop:h-8" />
                  </div>
                </div>
              </div>

              {/* Table Section */}
              <div className={`border rounded-xl overflow-hidden transition-all ${isDark ? 'bg-[#1e293b] border-white/10' : 'bg-white border-gray-200 shadow-sm'}`}>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[1000px] text-left border-collapse">
                    <thead>
                      <tr className="text-white text-xs desktop:text-sm uppercase font-semibold tracking-wider" style={{ backgroundColor: currentTheme.color }}>
                        <th className="py-3 px-6 desktop:py-4 desktop:px-8">Product Name</th>
                        <th className="py-3 px-6 desktop:py-4 desktop:px-8">Category</th>
                        <th className="py-3 px-6 desktop:py-4 desktop:px-8">Total Units</th>
                        <th className="py-3 px-6 desktop:py-4 desktop:px-8">Price</th>
                        <th className="py-3 px-6 desktop:py-4 desktop:px-8">Expiry Date</th>
                        <th className="py-3 px-6 desktop:py-4 desktop:px-8 text-center">Stock Validation</th>
                        <th className="py-3 px-6 desktop:py-4 desktop:px-8 text-center">Status</th>
                        <th className="py-3 px-6 desktop:py-4 desktop:px-8 text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody className={`${isDark ? 'divide-y divide-white/5' : 'divide-y divide-gray-100 text-sm desktop:text-base text-gray-700'}`}>
                      {currentProducts.length === 0 ? (
                        <tr>
                          <td colSpan="8" className="py-8 text-center text-gray-500">
                            {activeTab === 'products' ? 'No products found' : 'No kits found'}
                          </td>
                        </tr>
                      ) : (
                        currentProducts.map((product) => {
                          // Calculate total units for kits
                          const totalUnits = product.productType === 'kit' && product.kitProducts
                            ? product.kitProducts.reduce((sum, p) => sum + parseInt(p.quantity || 0), 0)
                            : (product.totalUnits ?? product.stock ?? 0);

                          return (
                            <tr key={product.id} className={`transition-colors duration-150 ${isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}>
                              <td className="py-3 px-6 desktop:py-4 desktop:px-8 font-medium">
                                <div className="flex items-center gap-3 desktop:gap-4">
                                  
                                  {/* ✅ Image with fallback */}
                                  {product.image ? (
                                    <img
                                      src={product.image}
                                      alt={product.name}
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'flex';
                                      }}
                                      className="w-10 h-10 desktop:w-14 desktop:h-14 rounded-lg object-cover border border-gray-100 bg-white flex-shrink-0"
                                    />
                                  ) : null}

                                  {/* Fallback icon */}
                                  <div
                                    className="w-10 h-10 desktop:w-14 desktop:h-14 rounded-lg border border-gray-100 bg-gray-50 items-center justify-center flex-shrink-0"
                                    style={{ display: product.image ? 'none' : 'flex' }}
                                  >
                                    <Package className="w-5 h-5 text-gray-300" />
                                  </div>

                                  <div>
                                    <span className={`truncate max-w-[200px] desktop:max-w-[300px] block font-bold ${isDark ? 'text-white' : 'text-gray-800'}`} title={product.name}>{product.name || product.title || 'Untitled'}</span>
                                    {product.productType === 'kit' && (
                                      <span className="text-[10px] font-bold uppercase tracking-tight" style={{ color: currentTheme.color }}>Kit/Combo</span>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td className={`py-3 px-6 desktop:py-4 desktop:px-8 ${isDark ? 'text-gray-400' : ''}`}>{product.category}</td>
                              <td className={`py-3 px-6 desktop:py-4 desktop:px-8 font-bold ${isDark ? 'text-gray-300' : ''}`}>{totalUnits}</td>
                              <td className="py-3 px-6 desktop:py-4 desktop:px-8 font-bold" style={{ color: currentTheme.color }}>₹ {product.price}</td>
                              <td className={`py-3 px-6 desktop:py-4 desktop:px-8 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{product.expiryDate}</td>
                              <td className="py-3 px-6 desktop:py-4 desktop:px-8 text-center">
                                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs desktop:text-sm font-medium border ${getStockStatusStyle(product.stockStatus)}`}>
                                  {product.stockStatus === 'Low Stock' && <AlertTriangle className="w-3 h-3 desktop:w-4 desktop:h-4 mr-1" />}
                                  {product.stockStatus === 'Expiry soon' && <Clock className="w-3 h-3 desktop:w-4 desktop:h-4 mr-1" />}
                                  {product.stockStatus === 'Out of stock' && <XCircle className="w-3 h-3 desktop:w-4 desktop:h-4 mr-1" />}
                                  {product.stockStatus === 'In Stock' && <Box className="w-3 h-3 desktop:w-4 desktop:h-4 mr-1" />}
                                  {product.stockStatus}
                                </span>
                              </td>
                              <td className="py-3 px-6 desktop:py-4 desktop:px-8 text-center">
                                <button
                                  onClick={() => toggleStatus(product.id)}
                                  className={`relative w-11 h-6 desktop:w-12 desktop:h-6 rounded-full transition-all duration-300 ease-in-out mx-auto border-2`}
                                  style={{ 
                                    backgroundColor: product.status ? currentTheme.color : '#ffffff',
                                    borderColor: isDark ? '#ffffff' : '#D1D5DB',
                                  }}
                                >
                                  <div className={`absolute top-[2px] left-[2px] w-4 h-4 desktop:w-4 desktop:h-4 rounded-full shadow-md transition-all duration-300 ease-in-out transform border-2 border-white ${
                                    product.status ? 'translate-x-5 desktop:translate-x-5 bg-white' : 'translate-x-0 bg-gray-400'
                                  }`}></div>
                                </button>
                              </td>
                              <td className="py-3 px-6 desktop:py-4 desktop:px-8 text-center relative">
                                <button
                                  onClick={() => toggleActionMenu(product.id)}
                                  className={`p-1.5 desktop:p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
                                >
                                  <MoreHorizontal className="w-5 h-5 desktop:w-6 desktop:h-6" />
                                </button>
                                {activeActionId === product.id && (
                                  <div className={`absolute right-8 top-10 w-36 desktop:w-44 rounded-lg shadow-xl border z-10 animate-scaleIn origin-top-right overflow-hidden ${
                                    isDark ? 'bg-[#1e293b] border-white/10' : 'bg-white border-gray-100'
                                  }`}>
                                    <button
                                      onClick={() => handleViewDetails(product)}
                                      className={`w-full text-left px-4 py-2.5 desktop:py-3 text-xs desktop:text-sm flex items-center gap-2 transition-colors ${
                                        isDark ? 'text-gray-300 hover:bg-white/5' : 'text-gray-700 hover:bg-gray-100'
                                      }`}
                                    >
                                      <Eye className="w-4 h-4 desktop:w-5 desktop:h-5" /> View Details
                                    </button>
                                    <button
                                      onClick={() => handleEditProduct(product)}
                                      className={`w-full text-left px-4 py-2.5 desktop:py-3 text-xs desktop:text-sm flex items-center gap-2 transition-colors ${
                                        isDark ? 'text-gray-300 hover:bg-white/5' : 'text-gray-700 hover:bg-gray-100'
                                      }`}
                                    >
                                      <Edit className="w-4 h-4 desktop:w-5 desktop:h-5" /> Edit
                                    </button>
                                    <div className={`h-px my-1 ${isDark ? 'bg-white/10' : 'bg-gray-100'}`}></div>
                                    <button
                                      onClick={() => handleDeleteProduct(product)}
                                      className="w-full text-left px-4 py-2.5 desktop:py-3 text-xs desktop:text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors font-medium"
                                    >
                                      <Trash2 className="w-4 h-4 desktop:w-5 desktop:h-5" /> Delete
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              
              {/* Pagination */}
              <Pagination 
                currentPage={currentPage}
                totalItems={productsToDisplay.length}
                itemsPerPage={itemsPerPage}
                onPageChange={(page) => setCurrentPage(page)}
              />
            </>
          )}

          {/* Delete Confirmation Modal */}
          {showDeleteModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 animate-fadeIn p-4">
              <div className={`${isDark ? 'bg-[#1e293b]' : 'bg-white'} rounded-xl shadow-2xl p-6 desktop:p-8 max-w-sm desktop:max-w-md w-full animate-scaleIn transform transition-all`}>
                <div className="flex flex-col items-center text-center">
                  <div className="w-12 h-12 desktop:w-16 desktop:h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                    <Trash2 className="w-6 h-6 desktop:w-8 desktop:h-8 text-red-600" />
                  </div>
                  <h3 className={`text-xl desktop:text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Delete "{productNameToDelete}"?</h3>
                  <p className={`${isDark ? 'text-gray-400' : 'text-gray-500'} mb-6 desktop:text-lg font-medium`}>
                    Are you sure you want to delete this product? All associated records might be affected.
                  </p>
                  <div className="flex gap-3 w-full">
                    <button
                      onClick={cancelDelete}
                      disabled={isDeleting}
                      className={`flex-1 px-4 py-2.5 desktop:py-3.5 border rounded-lg font-bold transition-all desktop:text-lg disabled:opacity-50 ${
                        isDark ? 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10' : 'bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={confirmDelete}
                      disabled={isDeleting}
                      className="flex-1 px-4 py-2.5 desktop:py-3.5 bg-red-600 text-white rounded-lg font-bold hover:bg-red-700 transition-colors shadow-lg shadow-red-200 desktop:text-lg disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {isDeleting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          Deleting...
                        </>
                      ) : 'Delete Now'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Bulk Upload Modal */}
          <BulkProductUploadModal
            isOpen={showBulkUploadModal}
            onClose={() => setShowBulkUploadModal(false)}
            onUploadSuccess={(result) => {
              message.success(`Successfully uploaded ${result.successCount} products!`);
              setShowBulkUploadModal(false);
              fetchProducts(); // ✅ List reload
            }}
          />
        </div>
      )}

      {/* Details and Edit views remain the same - truncated for brevity */}
      {/* Include the full details and edit sections from your original code */}
    </div>
  );
};

export default ProductList;